import { useEffect, useRef } from 'react';

/**
 * "You have unsaved changes" for this app's router (a BrowserRouter — no
 * data-router blockers). While `when` holds:
 *  - reload / tab close / typing a URL gets the browser's own prompt;
 *  - a click on any same-origin link (sidebar, back link, a document link)
 *    is stopped before the router sees it and `onBlocked(to)` decides —
 *    typically a confirm that then `navigate(to)`s.
 * Browser back / forward is not intercepted.
 */
export function useLeaveGuard(
  when: boolean,
  onBlocked: (to: string) => void
): void {
  const onBlockedRef = useRef(onBlocked);
  onBlockedRef.current = onBlocked;

  useEffect(() => {
    if (!when) return undefined;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Older browsers need a returnValue to show the prompt.
      e.returnValue = '';
    };

    const onClick = (e: MouseEvent) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      const target = e.target as Element | null;
      const a = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.hasAttribute('download')) return;
      if (a.target && a.target !== '_self') return;
      let url: URL;
      try {
        url = new URL(a.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      const to = `${url.pathname}${url.search}${url.hash}`;
      const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (to === here) return;
      // Capture phase on the document: runs before React's root listener,
      // so the router's <Link> never navigates.
      e.preventDefault();
      e.stopPropagation();
      onBlockedRef.current(to);
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [when]);
}
